import { useCallback, useEffect, useMemo, useState } from 'react'
import DataTable from 'react-data-table-component'
import { differenceBy, isEmpty } from 'lodash';
import { Box, Button, MenuItem, Select, Grid, Typography, Stack, FormControl } from '@mui/material';
import NoDataComponent from './NoDataComponent';
import SearchComponent from './SearchComponent';
import TableModals from './TableModals';
import { downloadCSV } from 'utils/exportCSV';
import { useGetInvoiceListMutation, useGetUserServiceListMutation, useUpdateUserServiceListMutation } from 'config/api/waV1_user';
import EditableForm_v2 from '../EditableForm_v2';
import ImageButton from '../ImageButton';
import { useGetFileMutation } from 'config/api/waV1_file';
import { useTranslation } from 'react-i18next';
import { KEY_COL_LABEL_AMOUNT, KEY_COL_LABEL_APPLIER, KEY_COL_LABEL_NAME, KEY_COL_LABEL_NAME_CN, KEY_COL_LABEL_PACKAGE, KEY_COL_LABEL_STATUS, KEY_COL_LABEL_UPDATEDAT, KEY_LABEL_DELETE, KEY_LABEL_EXPORT_AS, KEY_LABEL_EXPORT_AS_CSV, KEY_LABEL_EXPORT_AS_PDF, KEY_LABEL_EXPORT_FILE_FORMAT, KEY_LABEL_FORM_PIG_MESSAGE, KEY_LABEL_FORM_PIG_PLURAL, KEY_LABEL_FORM_PIG_ROWS_PER_PAGE, KEY_LABEL_FORM_PIG_SELECT_ALL, KEY_LABEL_FORM_PIG_SINGULAR, KEY_LABEL_FORM_TABLE_TITLE, KEY_LABEL_FORM_UPLOADED_FILE, KEY_NOTI_MSG_DELETE, MODULE_PREVIEW_ROUTER_FORM } from 'config/const/locale';
import getI18nStatusText, { STATUS } from 'utils/formStatus';
import { useSelector } from 'react-redux';
import { filters } from 'config/const/global';
import EditableForm_vo from '../EditableForm_vo';
import { ConstructionOutlined } from '@mui/icons-material';
import EditableForm_cs from '../EditableForm_cs';
import EditableForm_cs_1 from '../EditableForm_cs_1';
import EditableForm_excp from '../EditableForm_excp';
import EditableForm_excs from '../EditableForm_excs';
import EditableForm_extr from '../EditableForm_extr';

// table data
const tableData = []

function createRow(id, companyNameCn, companyName, applier, locale_name, amount, updated_at, status, extra) {
    return { id, companyNameCn, companyName, applier, locale_name, amount, updated_at, status, extra }
}

/**
 * react-data-table-component for ref: https://www.npmjs.com/package/react-data-table-component/v/6.11.8#header-context-menu-when-using-selectable-rows
 * export pdf for ref: jspdf(http://raw.githack.com/MrRio/jsPDF/master/docs/index.html) and jspdf-autotable(https://github.com/simonbengtsson/jsPDF-AutoTable)
 * @Todo i18n
 */
export default function CMSTableWithForm({
    filter
}) {

    const { t } = useTranslation(MODULE_PREVIEW_ROUTER_FORM)

    // columns definition
    const columns = [
        {
            id: 'id',
            name: 'id',
            selector: row => row.id,
            sortFunction: (a, b) => b?.id - a?.id,
            sortable: true,
            width: '64px',
        },
        {
            id: 'companyNameCn',
            name: t(KEY_COL_LABEL_NAME_CN),
            selector: row => row.companyNameCn,
        },
        {
            id: 'companyName',
            name: t(KEY_COL_LABEL_NAME),
            selector: row => row.companyName,
        },
        {
            id: 'applier',
            name: t(KEY_COL_LABEL_APPLIER),
            selector: row => row.applier,
        },
        {
            id: 'locale_name',
            name: t(KEY_COL_LABEL_PACKAGE),
            selector: row => row.locale_name,
            grow: 2
        },
        {
            id: 'amount',
            name: t(KEY_COL_LABEL_AMOUNT),
            selector: row => row.amount,
        },
        {
            id: 'updated_at',
            name: t(KEY_COL_LABEL_UPDATEDAT),
            selector: row => row.updated_at,
        },
        {
            id: 'status',
            name: t(KEY_COL_LABEL_STATUS),
            cell: (row, index, column, id) => {
                const handleChange = (e) => {
                    row?.extra?.updateUS({
                        id: row?.id,
                        payload: {
                            status: e.target.value
                        }
                    })
                        .then(res => {
                            window.location.reload()
                        })
                }
                return (
                    <FormControl fullWidth>
                        <Select
                            value={row?.status}
                            onChange={handleChange}
                            className='wa-no-border'
                        >
                            {
                                STATUS.map(
                                    i => <MenuItem key={i} value={i}>{getI18nStatusText(i)}</MenuItem>
                                )
                            }
                        </Select>
                    </FormControl>
                )
            }
        }
    ];
    const { lng } = useSelector(state => state.global)
    const [selectedRows, setSelectedRows] = useState([]); // save the selected row
    const [toggleCleared, setToggleCleared] = useState(false); // indicate whether hiding the context or not
    const [isEditTableData, setIsEditTableData] = useState(false) // indicate the row table's state: editable or uneditable
    const [allRowData, setAllRowData] = useState(tableData); // save the all table data
    const [isEdit, setIsEdit] = useState(false)
    const [isUpdate, setIsUpdate] = useState(false)
    let rowData = {} // save the data waiting to change
    const [getUSListFn] = useGetUserServiceListMutation()
    const [updateUSFn] = useUpdateUserServiceListMutation()
    const [getInvListFn] = useGetInvoiceListMutation()
    const [getFileFn] = useGetFileMutation()

    const [expandedRows, setExpandedRows] = useState({})
    const [editRowById, setEditRowById] = useState(0)
    const [files, setFiles] = useState([])

    const [filterText, setFilterText] = useState(''); // keyword to search
    const [filterType, setFilterType] = useState(columns[0]?.id) // type to search with keyword
    // const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

    // filter results when conditions meet
    let filteredItems = allRowData.filter(
        item => item[filterType] && String(item[filterType]).toLowerCase().includes(filterText.toLowerCase())
    );



    // save row table's input value
    const iptOnChangeHandler =
        (id, iptType) => (event) => {
            rowData = {
                id,
                type: iptType,
                value: event.target.value
            }
        }

    // Table's subHeader
    const subHeaderComponentMemo = useMemo(() => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: "100%" }}>
            {/* <div style={{ display: 'flex' }}> */}
            {/* <Button type="button">create</Button> */}
            {/* <TableModals columns={columns} dataSetter={setAllRowData} /> */}

            {/* <div style={{ paddingLeft: 16, marginLeft: 16, borderLeft: '1px solid black' }}> */}
            {/* <div>
                    <Select
                        variant='filled'
                        value={1}
                        disableUnderline
                        sx={{
                            '&>div': {
                                p: 1.5
                            }
                        }}
                    >
                        <MenuItem value={1} disabled hidden >{t(KEY_LABEL_EXPORT_AS)}</MenuItem>
                        <MenuItem onClick={() => downloadCSV(isEmpty(selectedRows) ? allRowData : selectedRows)}>{t(KEY_LABEL_EXPORT_AS_CSV)}</MenuItem> */}
            {/* <MenuItem onClick={() => exportPdf(selectedRows, allRowData)}>{t(KEY_LABEL_EXPORT_AS_PDF)}</MenuItem> */}
            {/* <MenuItem>SQL</MenuItem> */}
            {/* <MenuItem>JSON</MenuItem> */}
            {/* <MenuItem>XML</MenuItem> */}
            {/* <MenuItem>MS-Excel</MenuItem> */}
            {/* </Select>
                </div>
            </div > */}
            {/* <input placeholder='type keyword to search' onChange={e => setFilterText(e.target.value)} onClear={handleClear} filterText={filterText} /> */}
            < SearchComponent columns={columns} Kword={filterText} saveKeyword={setFilterText} KType={filterType} ChangeKType={setFilterType} />
        </div >
    ), [
        filterText,
        filterType,
        selectedRows,
        allRowData,
        lng
        // resetPaginationToggle
    ]);

    //save the selected rows data to state
    const handleRowSelected = useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    useEffect(() => {
        // const tempStr = localStorage.getItem('dummydata')
        // const datum = JSON.parse(tempStr)
        // if (datum) {
        //     setAllRowData(p => ([...p, {
        //         "id": p.length + 1,
        //         "name": datum.name,
        //         "phone": datum.tel,
        //         "email": datum.email,
        //         "package": datum.package
        //     }]))
        // }

        // getUSListFn()
        //     .then(
        //         res => {
        //             if (res.data?.data) {
        //                 setAllRowData(new Array(...res.data?.data?.user_services?.data).map(
        //                     i => createRow(i.id, i.company_name_zh, i.user.name, i.service.locale_name, i.service.price, i.updated_at, getI18nStatusText(i.status), { ...i, updateUS: updateUSFn })
        //                 ))
        //             }
        //         }
        //     )
        getInvListFn(filter)
            .then(
                res => {
                    if (res.data?.data) {
                        setAllRowData(new Array(...res.data?.data?.invoices?.data).map(
                            i => createRow(i.user_service?.id, i.user_service?.company_name_zh, i.user_service?.company_name_en, i.user?.name, i.user_service?.service.locale_name, i.total, i.updated_at, i.user_service?.status, { ...i, updateUS: updateUSFn })
                        ))
                    }
                }
            )
    }, [filter])

    useEffect(() => {

        handleGetFile(expandedRows?.extra?.user_service?.files)
    }, [expandedRows])

    // context component includes delete action
    const contextActions = useMemo(() => {
        const handleDelete = () => {

            if (window.confirm(t(KEY_NOTI_MSG_DELETE))) {
                setToggleCleared(!toggleCleared);
                setAllRowData(differenceBy(allRowData, selectedRows, 'id'));
            }
        };

        return (
            <Button
                key="delete"
                variant="danger"
                onClick={handleDelete}
                style={{ backgroundColor: 'red', color: 'white', fontSize: 14, fontWeight: 700 }} >
                {t(KEY_LABEL_DELETE)}
            </Button >
        );
    }, [allRowData, selectedRows, toggleCleared]);

    // save changed row data to state
    const confirmChange = useCallback(() => {
        if (isEmpty(rowData) && isEmpty(rowData?.id)) return setIsEditTableData(false)
        // setAllRowData(pre => pre.map(i => (i.id === rowData.id) ? set(i, rowData.type, rowData.value) : i))
        setAllRowData(pre => pre.map(i => (i.id === rowData.id) ? { ...i, [rowData?.type]: rowData?.value } : i))
        setIsEditTableData(false)
    }, [rowData, allRowData])


    const handleGetFile = useCallback((paths) => {
        setFiles([])
        // paths?.map(async i => {
        //     const data = await downloadFile(i.file_path)
        //     let label = i.file_path?.split('/')
        //     setFiles(p => [...p, { id: i.id, label: label[label.length - 1], data }])
        //     return data
        // })

        paths?.map(i => {
            getFileFn(i?.file_path)
                .then(data => {

                    let label = i.file_path?.split('/')
                    setFiles(p => [...p, { id: i.id, label: label[label.length - 1], type: i?.doc_type, data }])
                    return data
                })
                .catch(reason => {
                    console.log('err', reason)
                })
        })

    }, [files])

    // useEffect(() => {
    //     setFiles([...temp])
    // }, [temp])

    /**
     * expanded component ?? bad performance ??
     * @param {Object} data the expanded row data
     */
    const ExpandedComponent = useCallback(({ data }) => {

        const _buildEditForm = (type) => {
            switch (type) {
                case filters.vo:
                    return <EditableForm_vo {...data.extra} />
                case filters.company_secretary_company_secretary_services:
                    return <EditableForm_cs {...data.extra} />
                case filters.company_secretary_annual_return_service:
                    return <EditableForm_cs_1 {...data.extra} />
                case filters.va_business_name_card:
                    return <EditableForm_excp {...data.extra} />
                case filters.va_customized_seal:
                    return <EditableForm_excs {...data.extra} />
                case filters.va_trademark_registration:
                    return <EditableForm_extr {...data.extra} />

                default:
                    return <EditableForm_v2 {...data.extra} />
            }
        }

        return (
            <>
                {_buildEditForm(filter)}
                <Box sx={{ width: '80%', m: '0 auto', pt: 2, border: '1px solid black', fontSize: 16, lineHeight: 2 }}>
                    <Typography variant='h4' > {t(KEY_LABEL_FORM_UPLOADED_FILE)}</Typography>
                    <Box>
                        <Stack display={'flex'} direction={'row'} gap={2} flexWrap={'wrap'}>
                            <Grid container spacing={2}>
                                {
                                    files.map(i => <Grid item sm={10}><ImageButton key={i.id} data={i.data} label={i.label} type={i.type} /></Grid>)
                                }
                            </Grid>
                        </Stack>
                    </Box>
                </Box>
            </>
        )
    }, [files])

    // const actionsMemo = useMemo(() => <Export onExport={() => downloadCSV(data)} />, []);


    const handleChange = ({ allSelected, selectedCount, selectedRows }) => {
        // You can set state or dispatch with something like Redux so we can use the retrieved data
        console.log('Selected Rows: ', selectedRows);
    };

    // export pdf func waiting to optimize
    const exportPdf = useCallback(() => {
        import('jspdf').then((jsPDF) => {
            import('jspdf-autotable').then(() => {
                const doc = new jsPDF.default(0, 0);

                const exportColumns = columns.map((col) => ({ title: col.name, dataKey: col.id }));

                doc.autoTable(exportColumns, isEmpty(selectedRows) ? allRowData : selectedRows);
                doc.save(t(KEY_LABEL_EXPORT_FILE_FORMAT));
            });
        });
    }, [columns, selectedRows, allRowData])


    return (
        <DataTable
            title={t(KEY_LABEL_FORM_TABLE_TITLE)}

            subHeader
            subHeaderComponent={subHeaderComponentMemo}

            columns={columns}
            data={filteredItems}

            // selected row
            selectableRows
            selectableRowsHighlight
            selectableRowsVisibleOnly
            onSelectedRowsChange={handleRowSelected}
            clearSelectedRows={toggleCleared}

            // expanded row
            expandableRows
            expandableRowsComponent={ExpandedComponent}
            expandOnRowDoubleClicked
            expandableRowExpanded={(row) => expandedRows?.id === row.id ? true : false}
            onRowExpandToggled={(toggleState, row) => {
                // console.log(toggleState, row)
                if (toggleState) setExpandedRows(row)
            }}

            // sorting setting
            defaultSortFieldId={'id'}
            defaultSortAsc

            // context setting
            // contextComponent={<div>hello</div>}
            contextActions={contextActions}
            contextMessage={{
                singular: t(KEY_LABEL_FORM_PIG_SINGULAR),
                plural: t(KEY_LABEL_FORM_PIG_PLURAL),
                message: t(KEY_LABEL_FORM_PIG_MESSAGE)
            }}

            pagination
            paginationRowsPerPageOptions={[10, 20, 50, 100]}
            paginationComponentOptions={{
                rowsPerPageText: t(KEY_LABEL_FORM_PIG_ROWS_PER_PAGE),
                rangeSeparatorText: '/',
                noRowsPerPage: false,
                selectAllRowsItem: true,
                selectAllRowsItemText: t(KEY_LABEL_FORM_PIG_SELECT_ALL)
            }}

            noDataComponent={<NoDataComponent />}

            highlightOnHover
            striped
            responsive


        />
    )
}
